<template>
    <div>
        <!-- unit selection -->
        <div v-if="translatedUnitSelectionList?.length > 1">
            <WtcButton
                v-for="unit in translatedUnitSelectionList"
                :key="unit.key"
                :class="{ 'bg-brand px-2 !text-white !no-underline': selectedUnitKey === unit.key }"
                @click="selectedUnitKey = unit.key"
                >{{ unit.label }}</WtcButton
            >
        </div>
        <!-- result content -->
        <div class="mb-2 flex items-center justify-between">
            <div>
                <span>{{ getStrByLng().LABEL_RESULTS }}: </span>
                <span>{{ selectedUnit?.Info?.Date }}</span>
                <span>, {{ selectedUnit?.Info?.Time }}</span>
                <span v-if="selectedUnit?.Info?.Wind"
                    >, {{ getStrByLng().LABEL_WIND }} {{ selectedUnit?.Info?.Wind }}</span
                >
            </div>
            <div class="flex gap-3">
                <!-- splits button -->
                <WtcButton
                    v-if="selectedUnit?.Splits?.length > 0"
                    class="!text-brand-secondary"
                    :icon="faStopwatch"
                    @click="showSplits = !showSplits"
                    >{{ getStrByLng().LABEL_SPLITS }}</WtcButton
                >
                <!-- attempts button -->
                <WtcButton
                    v-if="isHeightDiscipline || isWidthDiscipline"
                    class="!text-brand-secondary"
                    :icon="faRepeat"
                    @click="showAttempts = !showAttempts"
                    >{{ getStrByLng().LABEL_ATTEMPTS }}</WtcButton
                >
                <!-- pdf list dropdown -->
                <WtcButton
                    v-if="Object.values(selectedUnit?.PDFs ?? {})?.length > 0"
                    class="!text-brand-secondary"
                    @click="toggle"
                    >{{ getStrByLng().LABEL_DOWNLOAD }}</WtcButton
                >
                <Popover ref="ddPdf">
                    <div class="border border-black/50 bg-white px-3 py-1 text-sm">
                        <WtcButton
                            v-if="selectedUnit?.PDFs?.ResultList"
                            class="text-brand-secondary my-1"
                            :href="getPdfUrl(selectedUnit?.PDFs?.ResultList)"
                            :icon="faFilePdf"
                            target="_blank"
                            >{{ getStrByLng().BTN_PDF_RESULTLIST }}</WtcButton
                        >
                        <WtcButton
                            v-if="selectedUnit?.PDFs?.RaceAnalysis"
                            class="text-brand-secondary my-1"
                            :href="getPdfUrl(selectedUnit?.PDFs?.RaceAnalysis)"
                            :icon="faFilePdf"
                            target="_blank"
                            >{{ getStrByLng().BTN_PDF_RACE_ANALYSIS }}</WtcButton
                        >
                    </div>
                </Popover>
            </div>
        </div>

        <div class="relative">
            <!-- splits table -->
            <WtcTransition :duration-ms="100" name="slide-down">
                <div v-if="showSplits" class="max-tablet:overflow-x-auto mb-3 border border-black/10 p-2">
                    <div class="mb-1">{{ getStrByLng().LABEL_SPLITS }}</div>
                    <table class="w-full border border-black/10">
                        <thead>
                            <tr>
                                <th class="w-28 min-w-28 text-left">{{ getStrByLng().HEADER_DISTANCE }}</th>
                                <th class="min-w-48 text-left">{{ getStrByLng().HEADER_ATHLETE }}</th>
                                <th class="w-28 min-w-28 text-right">{{ getStrByLng().HEADER_TIME }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="split in selectedUnit?.Splits" :key="split.LeadBib">
                                <td class="text-left">{{ split.Distance }}</td>
                                <td class="text-left">
                                    <WtcName :competitor-name="{ Name: split.LeadAthlete }" />
                                </td>
                                <td class="text-right">{{ split.LeadPerf }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </WtcTransition>
            <!-- results table -->
            <div class="max-tablet:overflow-x-auto">
                <table class="w-full border border-black/10">
                    <thead>
                        <tr>
                            <th class="sticky left-0 w-10 min-w-10">{{ getStrByLng().HEADER_RANK }}</th>
                            <th class="sticky left-0 min-w-48 text-left">{{ getStrByLng().HEADER_NAME }}</th>
                            <!-- attempts columns -->
                            <template v-if="showAttempts">
                                <!-- height attempts -->
                                <template v-if="isHeightDiscipline">
                                    <th v-for="attempt in selectedUnit?.HeightTable" :key="attempt.Pos">
                                        {{ attempt.Value }}
                                    </th>
                                </template>
                                <!-- width attempts -->
                                <template v-if="isWidthDiscipline">
                                    <th v-for="attemptNo in widthTableColumnAmountMax" :key="attemptNo">
                                        {{ attemptNo }}
                                    </th>
                                </template>
                            </template>
                            <template v-else>
                                <th class="w-24 min-w-24 text-left">{{ getStrByLng().HEADER_RESULT }}</th>
                                <th v-if="selectedUnit?.Info?.Diamond_Race" class="w-24 min-w-24">
                                    {{ getStrByLng().HEADER_DP }}
                                </th>
                                <th v-if="selectedUnit?.Info?.Diamond_Race" class="w-24 min-w-24">
                                    {{ getStrByLng().HEADER_DR }}
                                </th>
                            </template>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="result in selectedUnit?.Results" :key="result.Bib">
                            <td class="sticky left-0">{{ result.Rank }}</td>
                            <td class="sticky left-0 text-left">
                                <div class="flex">
                                    <NocFlag class="mr-1" :nat-code="result.Nation" />
                                    <WdlArchiveName
                                        :competitor="{
                                            FirstName: result.Firstname,
                                            LastName: result.Lastname,
                                            AthleteId: result.DL_ID
                                        }"
                                    ></WdlArchiveName>
                                </div>
                            </td>
                            <!-- attempts columns -->
                            <template v-if="showAttempts">
                                <!-- height attempts -->
                                <template v-if="isHeightDiscipline">
                                    <td v-for="attempt in result?.RoundResults?.RoundResult" :key="attempt.Round">
                                        {{ attempt.Performance }}
                                    </td>
                                    <td
                                        v-for="fillerCol in heightTableColumnAmount - result?.RoundResults?.__Count"
                                        :key="fillerCol"
                                    ></td>
                                </template>
                                <!-- width attempts -->
                                <template v-if="isWidthDiscipline">
                                    <td v-for="attempt in result?.RoundResults?.RoundResult" :key="attempt.Round">
                                        {{ attempt.Performance }}
                                    </td>
                                    <td
                                        v-for="fillerCol in widthTableColumnAmountMax - result?.RoundResults?.__Count"
                                        :key="fillerCol"
                                    ></td>
                                </template>
                            </template>
                            <template v-else>
                                <CompetitorResultColumn class="text-right" :result="result" />
                                <td v-if="selectedUnit?.Info?.Diamond_Race">{{ result.DR_Points }}</td>
                                <td v-if="selectedUnit?.Info?.Diamond_Race">{{ result.DR_Rank }}</td>
                            </template>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { faFilePdf, faRepeat, faStopwatch } from "@fortawesome/free-solid-svg-icons";
import { useTranslatedProperty } from "@swisstiming/owl-core";
import { getStrByLng, WtcButton, WtcName, WtcTransition } from "@swisstiming/webtec-kit";
import Popover from "primevue/popover";
import { computed, onMounted, ref } from "vue";

import { useInjectedSelections } from "../composables/provide.composables";
import { IInfo, IUnit } from "../interfaces/wdl-archive.interfaces";
import { useArchiveStore } from "../stores/wdl-archive.store";
import CompetitorResultColumn from "./elements/CompetitorResultColumn.vue";
import NocFlag from "./elements/NocFlag.vue";
import WdlArchiveName from "./elements/WdlArchiveName.vue";

const props = withDefaults(
    defineProps<{
        unitsDictionary?: { [x: string]: IUnit };
    }>(),
    {}
);

const ddPdf = ref();
const showSplits = ref(false);
const showAttempts = ref(false);
const selectedUnitKey = ref<string>();

// unit selection
const translatedUnitSelectionList = computed(() =>
    Object.entries(props.unitsDictionary ?? {})?.map(([key, unit]) => {
        return {
            key,
            // if a unit does not provide a round name, use the translated static 'Result'-label instead
            label: useTranslatedProperty<IInfo>(unit.Info, "Round_Name", "Round_Names") ?? getStrByLng().HEADER_RESULT
        };
    })
);

const selectedUnit = computed(() => props.unitsDictionary?.[selectedUnitKey.value]);

// select first unit initially
onMounted(() => {
    selectedUnitKey.value = Object.keys(props.unitsDictionary ?? {})?.at(0);
});

// pdf URLs
const archiveStore = useArchiveStore();
const { selectedSeason, selectedMeeting } = useInjectedSelections();

const getPdfUrl = (pdfFileName: string) =>
    archiveStore.getPdfUrl(selectedSeason.value, selectedMeeting.value, pdfFileName);

// height disciplines are pole-vault, high-jump, etc.
const heightTableColumnAmount = computed(() => selectedUnit.value?.HeightTable?.length ?? 0);
const isHeightDiscipline = computed(() => heightTableColumnAmount.value > 0);

// width disciplines are triple-jump, long-jump, shot-put, etc.
const widthTableColumnAmountMax = computed(() =>
    Math.max(...(selectedUnit.value?.Results?.map((result) => result.RoundResults?.__Count) ?? []))
);
const isWidthDiscipline = computed(() => !isHeightDiscipline.value && widthTableColumnAmountMax.value > 0);

const toggle = (event) => {
    ddPdf.value.toggle(event);
};
</script>

<style lang="scss"></style>
